import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

import { Observable, Subscription } from "rxjs";

import { Pending } from "../../../shared-library/decorators/pending.decorator";
import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { DisposeMixin, EmptyClassInstance } from "../../../shared-library/mixins/dispose.mixin";
import { LockMixin } from "../../../shared-library/mixins/lock.mixin";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";

import { BaseSearchTabConfigModel } from "../../models/base-search-tab-config.model";
import { PaginationOptionsFactoryService } from "../../services/pagination-options-factory.service";

import { GoogleMapComponent } from "./components/google-map/google-map.component";
import { DirectorySearchForm } from "./forms/directory-search.form";
import { DirectoryItemModel } from "./models/directory-item.model";
import { DirectorySearchParametersModel } from "./models/directory-search-parameters.model";
import { SearchResultsModel } from "./models/search-results.model";
import { SearchTabConfigModel } from "./models/search-tab-config.model";
import { DirectorySearchParametersFactoryService } from "./services/directory-search-parameters-factory.service";
import { DirectorySearchService } from "./services/directory-search.service";
import { PostcodesGeolocationService} from "../../../shared-library/services/geolocation/postcodes-geolocation.service";
import { PostcodesModel} from "../../../shared-library/models/geolocation/postcodes-model";
import { ExtraExecutionResultModel} from "../../../shared-library/models/api/extra-execution-result.model";
import {ContentSearchResultsModel} from "../content-search/models/content-search-results.model";
import {ContentSearchParametersModel} from "../content-search/models/content-search-parameters.model";
import {ContentSearchService} from "../content-search/services/content-search.service";
import {MarketplaceSearchResultsModel} from "../marketplace-search/models/marketplace-search-results.model";
import {MarketplaceSearchParametersModel} from "../marketplace-search/models/marketplace-search-parameters.model";
import {MarketplaceSearchService} from "../marketplace-search/services/marketplace-search.service";
import {SearchTabAlias} from "../../enums/search-tab-alias.enum";
import {SearchTabFilterModel} from "./models/search-tab-filter.model";
import {MarketplaceItemModel} from "../marketplace-search/models/marketplace-item.model";
import {BaseSearchParametersModel} from "../../models/base-search-parameters.model";
import {
    QueryStringSerializationService
} from "../../../shared-library/services/environment/query-string-serialization.service";

declare function externalUpdateActiveSearchTabFn(searchTabAlias: string): any;
declare function externalUpdatePostcodeAndDistanceFn(): any;

@Component({
    selector: "pcg-ng-directory-search",
    template: require('./directory-search.component.html')
})
export class DirectorySearchComponent extends DisposeMixin(LockMixin(EmptyClassInstance)) implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public searchTabConfig: SearchTabConfigModel;

    @Input()
    public onTabActivated: Observable<BaseSearchTabConfigModel>;

    @Input()
    public searchPageConfig: Array<BaseSearchTabConfigModel>;

    @Output()
    public changeActiveTabDelegate = new EventEmitter<BaseSearchTabConfigModel>();

    @ViewChild("mapContainer", { static: false })
    public mapContainerRef: GoogleMapComponent;

    @ViewChild("contentInformationCollapseButton", { static: false })
    public contentInformationCollapseButtonRef: ElementRef;

    @ViewChild("contentInformationCollapseBlock", { static: false })
    public contentInformationCollapseBlockRef: ElementRef;

    @ViewChild("marketplaceInformationCollapseButton", { static: false })
    public marketplaceInformationCollapseButtonRef: ElementRef;

    @ViewChild("marketplaceInformationCollapseBlock", { static: false })
    public marketplaceInformationCollapseBlockRef: ElementRef;

    public readonly contentBlockSize = 2;
    public searchForm: DirectorySearchForm;
    public searchResult: SearchResultsModel;
    public contentSearchResult: ContentSearchResultsModel;
    public marketplaceSearchResult: MarketplaceSearchResultsModel;
    public marketplaceInfoBoxId: string;
    public contentInfoBoxId: string;
    public tabAlias = SearchTabAlias;
    public tabFilter: Array<SearchTabFilterModel> = new Array<SearchTabFilterModel>();

    public postcodeControl: FormControl;
    public distanceControl: FormControl;

    public isMapInitialized: boolean = false;
    public isMapUpdated: boolean = false;
    public isMapVisible: boolean = true;
    public isContentInfoBoxCollapsed: boolean = true;
    public isMarketplaceInfoBoxCollapsed: boolean = true;

    public paginationOptions: PaginationOptionsModel;

    @Pending
    private _searchInProgressSubscription: Subscription;

    private _formChangedSubscription: Subscription;
    private _formSubmittedSubscription: Subscription;
    private _tabActivatedSubscription: Subscription;

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _geolocationService: PostcodesGeolocationService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: DirectorySearchParametersFactoryService,
        private _searchService: DirectorySearchService,
        private _contentSearchService: ContentSearchService,
        private _marketplaceSearchService: MarketplaceSearchService,
        private _serializationService: QueryStringSerializationService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();

        const initialSearchParams = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString(), this.searchTabConfig);
        const emptySearchParams = this._searchParametersFactory.createFromQueryString("", this.searchTabConfig);

        this.searchForm = DirectorySearchForm.createForm(initialSearchParams, emptySearchParams);

        this.postcodeControl = new FormControl(this.searchForm.postcode.value);
        this.distanceControl = new FormControl(this.searchForm.distance.value);

        this._formChangedSubscription = this.searchForm.onFormChanged.subscribe(
            (searchParams: DirectorySearchParametersModel) => {
                return this.invokeSearch(searchParams);
            }
        );

        this._formSubmittedSubscription = this.searchForm.onFormSubmitted.subscribe(
            (searchParams: DirectorySearchParametersModel) => {
                return this.invokeSearch(searchParams);
            }
        );

        this._tabActivatedSubscription = this.onTabActivated.subscribe((tab) => this.onTabActivatedHandler(tab));
        this.contentInfoBoxId = this.generateUniqueId();
        this.marketplaceInfoBoxId = this.generateUniqueId();
        this.buildTabFilter();
        (window as any).getSearchFilters = this.getSearchFilters.bind(this);
    }

    public ngAfterViewInit(): void {
        this.mapContainerRef.initializeMap();
        this.searchForm.submit();
    }

    public ngOnDestroy(): void {
        this.dispose();
        this.searchForm.dispose();
    }

    public onSearchButtonClicked(): void {
        if (this.postcodeControl.value && this.distanceControl.value) {
            this._geolocationService.getCoordinatesByPostcode(this.postcodeControl.value).subscribe(
                (location: ExtraExecutionResultModel<PostcodesModel>) => {
                    if (location) {
                        this.mapContainerRef.setMapCenter(location.result.latitude, location.result.longitude);
                    }
                },
                () => {
                }
            );

            this.searchForm.pageNumber.setValue(1, { emitEvent: false });
            this.searchForm.postcode.setValue(this.postcodeControl.value, { emitEvent: false });
            this.searchForm.distance.setValue(this.distanceControl.value, { emitEvent: false });
            if (this.searchForm.sortOption.untouched) {
                this.searchForm.sortOption.setValue("Distance;asc", { emitEvent: false });
            }

            this.searchForm.submit();
            externalUpdatePostcodeAndDistanceFn();
        }
    }

    private onTabActivatedHandler(activatedTab: BaseSearchTabConfigModel): void {
        if (activatedTab.tabAlias === this.searchTabConfig.tabAlias) {
            this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(this.searchForm.formValue));
            externalUpdateActiveSearchTabFn(activatedTab.tabAlias);
            this.buildTabFilter();
        } else {
            const searchString = this._browserWindowService.getQueryString();
            const searchParams = this._serializationService.deserialize<BaseSearchParametersModel>(searchString);
            if (searchParams && searchParams.searchTab !== activatedTab.tabAlias) {
                externalUpdateActiveSearchTabFn(activatedTab.tabAlias);
            }
        }
    }

    private invokeSearch(searchParams: DirectorySearchParametersModel): void {
        // NOTE: temporary workaround
        // searchParams.filters = new SearchFiltersModel();
        // searchParams.filters.categories = searchParams.categories;
        // searchParams.filters.areas = searchParams.areas;
        // searchParams.filters.postcode = searchParams.postcode;
        // searchParams.filters.distance = searchParams.distance;

        if (this.searchTabConfig.isActive) {
            this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(searchParams));
        }

        this._searchInProgressSubscription = this._searchService.getSearchResults(searchParams).subscribe(
            (result: SearchResultsModel) => {
                if (result && result.pageItems) {
                    result.pageItems = result.pageItems.map(model => new DirectoryItemModel(model));
                }

                this.searchResult = result;

                window.scrollTo(0, 0);

                this.mapContainerRef.updateMapMarkers(this.searchResult.pageItems);
            },
            () => {
            }
        );

        const contentParameters = new ContentSearchParametersModel();
        contentParameters.searchTab = this.tabAlias.CONTENT_SEARCH;
        contentParameters.searchText = searchParams.searchText;
        contentParameters.pageSize = this.contentBlockSize;
        contentParameters.pageNumber = 1;
        contentParameters.sortOption = "0";
        contentParameters.searchContext =this.searchTabConfig.contentSearchContext;
        this._contentSearchService.getSearchResults(contentParameters).subscribe(
            (result: ContentSearchResultsModel) => {
                if (result.recommendedLinks.length > this.contentBlockSize) {
                    result.recommendedLinks = result.recommendedLinks.slice(0, this.contentBlockSize);
                }
                result.pageItems = result.pageItems.slice(0, this.contentBlockSize - result.recommendedLinks.length);
                this.contentSearchResult = result;

                setTimeout(() => {
                    const isHidden = this.isContentInfoBoxCollapsed || this.isMapVisible || searchParams.postcode || searchParams.categories.length;
                    this.isContentInfoBoxCollapsed = this.setInfoBlockVisibility(!isHidden, this.contentInformationCollapseButtonRef.nativeElement, this.contentInformationCollapseBlockRef.nativeElement);
                });
            }
        );

        const marketplaceParameters = new MarketplaceSearchParametersModel();
        marketplaceParameters.searchTab = this.tabAlias.MARKETPLACE_SEARCH;
        marketplaceParameters.searchText = searchParams.searchText;
        marketplaceParameters.pageSize = this.contentBlockSize;
        marketplaceParameters.pageNumber = 1;
        marketplaceParameters.sortOption = "Rank;asc";
        this._marketplaceSearchService.getSearchResults(marketplaceParameters).subscribe(
            (result: MarketplaceSearchResultsModel) => {
                if (result.recommendedLinks.length > this.contentBlockSize) {
                    result.recommendedLinks = result.recommendedLinks.slice(0, this.contentBlockSize);
                }
                result.pageItems = result.pageItems.slice(0, this.contentBlockSize - result.recommendedLinks.length);
                this.marketplaceSearchResult = result;

                setTimeout(() => {
                    const isHidden = this.isMarketplaceInfoBoxCollapsed || this.isMapVisible || searchParams.postcode || searchParams.categories.length;
                    this.isMarketplaceInfoBoxCollapsed = this.setInfoBlockVisibility(!isHidden, this.marketplaceInformationCollapseButtonRef.nativeElement, this.marketplaceInformationCollapseBlockRef.nativeElement);
                });
            }
        );
    }

    private setInfoBlockVisibility(isVisible: boolean, collapseButton: any, collapseBlock: any): boolean {
        if (isVisible) {
            collapseButton.setAttribute("attr.aria-expanded", true);
            collapseButton.classList.remove("collapsed");
            collapseBlock.classList.add("show");
            return false;
        } else {
            collapseButton.setAttribute("attr.aria-expanded", false);
            collapseButton.classList.add("collapsed");
            collapseBlock.classList.remove("show");
            return true;
        }
    }

    public onContentInfoBlockButtonClicked(event: Event): void {
        event.preventDefault();
        this.isContentInfoBoxCollapsed = !this.isContentInfoBoxCollapsed;
    }

    public onContentInfoBlockSeeAllResultsButtonClicked(event: Event): void {
        event.preventDefault();

        const contentSearchTab = this.searchPageConfig.find(tab => tab.tabAlias === this.tabAlias.CONTENT_SEARCH);
        this.onActiveTabChangedDelegate(contentSearchTab);
    }

    private onActiveTabChangedDelegate(activeTab: BaseSearchTabConfigModel): void {
        this.changeActiveTabDelegate.emit(activeTab);
    }
    public onMarketplaceInfoBlockButtonClicked(event: Event): void {
        event.preventDefault();
        this.isMarketplaceInfoBoxCollapsed = !this.isMarketplaceInfoBoxCollapsed;
    }

    public onMarketplaceInfoBlockSeeAllResultsButtonClicked(event: Event): void {
        event.preventDefault();

        const marketplaceSearchTab = this.searchPageConfig.find(tab => tab.tabAlias === this.tabAlias.MARKETPLACE_SEARCH);
        this.onActiveTabChangedDelegate(marketplaceSearchTab);
    }

    public generateUniqueId(): string {
        const dateStr = Date
            .now()
            .toString(36);

        const randomStr = Math
            .random()
            .toString(36)
            .substring(2, 8);

        return `${dateStr}-${randomStr}`;
    }

    private buildTabFilter(): void {
        this.tabFilter = [];

        this.searchPageConfig.forEach(tab => {
            const checkboxControl = new SearchTabFilterModel();
            checkboxControl.tab = tab;
            checkboxControl.isSelected = tab.tabAlias === this.searchTabConfig.tabAlias;

            this.tabFilter.push(checkboxControl);
        });
    }

    public onToggleMapButtonClicked(event: Event): void {
        event.preventDefault();

        if (!this.isMapInitialized) {
            this.mapContainerRef.initializeMap();
            this.isMapInitialized = true;
        }

        setTimeout(() => {
            this.isMapVisible = !this.isMapVisible;

            if (this.isMapVisible && !this.isMapUpdated) {
                this.mapContainerRef.updateMapMarkers(this.searchResult.pageItems);
                this.isMapUpdated = true;
            }
        });
    }

    public getMarketplaceItemDetailsPageUrl(item: MarketplaceItemModel, includeReturnUrl: boolean = true): string {
        return `${this.searchTabConfig.marketplaceSearchPageUrl}?supplierId=${item.supplierId}&bcgId=${item.catalogueId}&productId=${item.id}` + (includeReturnUrl ? `&returnUrl=${encodeURIComponent(window.location.href)}` : "");
    }
    public getSearchFilters(): string {
        const searchTabCaption = this.searchTabConfig?.tabCaption ? this.searchTabConfig.tabCaption : "";
        const postcode = this.searchForm?.postcode.value ? this.searchForm.postcode.value : "";
        const categoriesId = this.searchForm?.categories.value;
        const filter = this.searchTabConfig.searchFilters.find(item => item.alias === "categories");
        const categoryNames: Array<string> = [];
        if (filter && categoriesId) {
            filter.attributes?.forEach(item => {
               if (categoriesId.includes(item.id)) {
                 categoryNames.push(item.name);
               }
               item.nestedAttributes?.forEach(nestedItem => {
                  if (categoriesId.includes(nestedItem.id)) {
                      categoryNames.push(nestedItem.name);
                  }
               });
            });
        }
        const categories = categoryNames.join(", ");

        const result = [searchTabCaption, postcode, categories].filter(str => str !== "").join(", ");
        return result;
    }
}
